"use client";

import { ArrowLeft, Calendar, User } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";

function SinglePostContent() {
  const { posts, loading, error } = usePosts();

  if (loading) {
    return (
      <div className="mx-auto max-w-4xl px-4 py-12">
        <div className="h-10 w-3/4 rounded-md bg-muted/40 animate-pulse mb-6" />
        <div className="h-6 w-1/2 rounded-md bg-muted/30 animate-pulse mb-8" />
        <div className="h-80 w-full rounded-md bg-muted/40 animate-pulse mb-8" />
        <div className="space-y-3">
          <div className="h-4 w-full rounded bg-muted/30 animate-pulse" />
          <div className="h-4 w-full rounded bg-muted/30 animate-pulse" />
          <div className="h-4 w-2/3 rounded bg-muted/30 animate-pulse" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="mx-auto max-w-4xl px-4 py-12 text-center">
        <p className="text-muted-foreground font-default">Unable to load this tour update.</p>
      </div>
    );
  }

  const post = posts[0];

  if (!post) {
    return (
      <div className="mx-auto max-w-4xl px-4 py-12 text-center">
        <p className="text-muted-foreground font-default">Update not found.</p>
        <Link to="/EclipsePage" className="mt-4 inline-block">
          <Button variant="outline" className="mt-6 border-primary text-primary hover:bg-primary hover:text-primary-foreground rounded-md">
            <ArrowLeft className="size-4" />
            Back to The Eclipse
          </Button>
        </Link>
      </div>
    );
  }

  const formattedDate = post.date
    ? new Date(post.date).toLocaleDateString(undefined, {
        year: "numeric",
        month: "long",
        day: "numeric",
      })
    : "";

  return (
    <article className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 py-12 lg:py-16">
      <div className="mb-8">
        <Link to="/EclipsePage" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-primary transition-colors font-default">
          <ArrowLeft className="size-4" />
          Back to tour updates
        </Link>
      </div>

      {post.categories && post.categories.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-5">
          {post.categories.map((cat, i) => (
            <span
              data-index={i}
              key={cat.id}
              className="inline-flex items-center rounded-md bg-primary/10 px-3 py-1 text-xs font-bold uppercase tracking-widest text-primary"
            >
              {cat.name}
            </span>
          ))}
        </div>
      )}

      <h1 className="font-default text-4xl sm:text-5xl lg:text-6xl font-bold text-foreground leading-tight tracking-tighter mb-6">
        {post.title}
      </h1>

      <div className="flex flex-wrap items-center gap-4 sm:gap-6 mb-10 text-sm text-muted-foreground font-default">
        {post.author && (
          <div className="flex items-center gap-3">
            <Avatar className="size-10 rounded-md">
              {post.author.avatar && (
                <AvatarImage src={post.author.avatar["48"] || post.author.avatar["96"]} alt={post.author.name} />
              )}
              <AvatarFallback className="bg-primary text-primary-foreground text-sm font-bold rounded-md">
                {post.author.name?.charAt(0) || "E"}
              </AvatarFallback>
            </Avatar>
            <div className="flex flex-col">
              <span className="text-foreground font-semibold">{post.author.name}</span>
              <span className="text-xs text-primary">Artist</span>
            </div>
          </div>
        )}
        {formattedDate && (
          <div className="flex items-center gap-2">
            <Calendar className="size-4 text-primary" />
            <time dateTime={post.date}>{formattedDate}</time>
          </div>
        )}
      </div>

      {post.featuredImage && (
        <div className="mb-10 overflow-hidden rounded-md shadow-xl border border-border/50">
          <img
            src={post.featuredImage}
            alt={post.title}
            className="w-full h-auto object-cover aspect-[16/9]"
            loading="lazy"
            decoding="async"
          />
        </div>
      )}

      {post.content && (
        <div
          className="prose prose-lg max-w-none text-foreground/90 font-default leading-relaxed [&_p]:mb-5 [&_h2]:font-default [&_h2]:text-3xl [&_h2]:font-bold [&_h2]:mt-10 [&_h2]:mb-4 [&_h2]:text-primary [&_h3]:font-default [&_h3]:text-2xl [&_h3]:font-semibold [&_h3]:mt-8 [&_h3]:mb-3 [&_a]:text-primary [&_a]:underline [&_a]:underline-offset-4 [&_a]:decoration-primary/30 [&_a]:decoration-2 hover:[&_a]:decoration-primary [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:mb-5 [&_ol]:list-decimal [&_ol]:pl-6 [&_ol]:mb-5 [&_li]:mb-2 [&_blockquote]:border-l-4 [&_blockquote]:border-primary [&_blockquote]:pl-6 [&_blockquote]:italic [&_blockquote]:bg-accent/30 [&_blockquote]:py-4 [&_blockquote]:my-6 [&_img]:rounded-md [&_img]:my-6"
          dangerouslySetInnerHTML={{ __html: post.content }}
        />
      )}

      {post.author && (
        <Card className="mt-16 bg-card border border-border/50 rounded-md shadow-lg">
          <CardContent className="flex flex-col sm:flex-row items-start sm:items-center gap-5 px-6 py-8">
            <Avatar className="size-16 shrink-0 rounded-md">
              {post.author.avatar && (
                <AvatarImage src={post.author.avatar["96"] || post.author.avatar["48"]} alt={post.author.name} />
              )}
              <AvatarFallback className="bg-primary text-primary-foreground text-lg font-bold rounded-md">
                {post.author.name?.charAt(0) || "E"}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1">
              <p className="text-xs uppercase tracking-widest text-primary font-bold mb-1 flex items-center gap-2">
                <User className="size-3" />
                The Artist
              </p>
              <h3 className="font-default text-2xl font-bold text-foreground mb-2">
                {post.author.name}
              </h3>
              <p className="text-sm text-muted-foreground font-default">
                Capturing the neon pulse of the city and the quiet moments between the strobe lights. Welcome to the world of The Eclipse.
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {post.tags && post.tags.length > 0 && (
        <div className="mt-10 pt-8 border-t border-border/20">
          <p className="text-xs uppercase tracking-widest text-muted-foreground mb-3 font-bold">Tour Tags</p>
          <div className="flex flex-wrap gap-2">
            {post.tags.map((tag, i) => (
              <span
                data-index={i}
                key={tag.id}
                className="inline-flex items-center rounded-md bg-secondary px-3 py-1.5 text-xs font-medium text-foreground hover:bg-primary/20 transition-colors cursor-default"
              >
                #{tag.name}
              </span>
            ))}
          </div>
        </div>
      )}
    </article>
  );
}

function RelatedPostsConsumer() {
  const { posts, loading } = usePosts();

  if (loading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {[...Array(3)].map((_, i) => (
          <div data-index={i} key={i} className="h-72 rounded-md bg-muted/30 animate-pulse" />
        ))}
      </div>
    );
  }

  if (posts.length === 0) {
    return (
      <p className="text-center text-muted-foreground font-default">No more tour stories yet.</p>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      {posts.map((post, i) => (
        <Link
          data-index={i}
          key={post.id}
          to={post.link || "#"}
          className="group block"
        >
          <Card className="h-full overflow-hidden bg-card border border-border/50 shadow-md hover:shadow-primary/10 transition-all duration-300 hover:-translate-y-1 rounded-md">
            {post.featuredImage && (
              <div className="aspect-[4/3] overflow-hidden">
                <img
                  src={post.featuredImage}
                  alt={post.title}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                  loading="lazy"
                  decoding="async"
                />
              </div>
            )}
            <CardContent className="px-6 py-6">
              {post.categories && post.categories.length > 0 && (
                <span className="inline-block text-xs font-bold uppercase tracking-widest text-primary mb-2">
                  {post.categories[0].name}
                </span>
              )}
              <h3 className="font-default text-xl font-bold text-foreground mb-2 line-clamp-2 group-hover:text-primary transition-colors">
                {post.title}
              </h3>
              {post.excerpt && (
                <div
                  className="text-sm text-muted-foreground line-clamp-2 font-default"
                  dangerouslySetInnerHTML={{ __html: post.excerpt }}
                />
              )}
              <div className="mt-4 inline-flex items-center gap-1 text-sm font-bold text-primary uppercase tracking-wider">
                Read Story
                <span aria-hidden className="transition-transform group-hover:translate-x-1">→</span>
              </div>
            </CardContent>
          </Card>
        </Link>
      ))}
    </div>
  );
}

export default function SinglePostTemplateSection() {
  const currentWPQueryParams =
    typeof window !== "undefined" && (window as any).wvcClient
      ? (window as any).wvcClient.getCurrentWPQueryParams()
      : { post_type: "post" };
  const wp_query = new WP_Query(currentWPQueryParams);

  return (
    <section data-section-id="1189" id="single-post-template-section" className="bg-background text-foreground">
      <PostsProvider wp_query={wp_query}>
        <SinglePostContent />
      </PostsProvider>

      <div className="bg-secondary/30 py-16 lg:py-20 border-t border-border/20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mb-10 flex flex-col sm:flex-row items-start sm:items-end justify-between gap-4">
            <div>
              <p className="text-xs uppercase tracking-widest text-primary mb-2 font-bold">
                More from the Tour
              </p>
              <h2 className="font-default text-3xl sm:text-4xl font-bold text-foreground tracking-tight">
                Behind the Scenes
              </h2>
            </div>
            <Link to="/EclipsePage">
              <Button variant="outline" className="border-primary text-primary bg-transparent hover:bg-primary hover:text-primary-foreground rounded-md font-bold uppercase tracking-wider text-xs">
                View All Updates
              </Button>
            </Link>
          </div>

          <PostsProvider wp_query={{ post_type: "post", per_page: 3, orderby: "date", order: "desc" }}>
            <RelatedPostsConsumer />
          </PostsProvider>
        </div>
      </div>
    </section>
  );
}